import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';

import { useWorkersService } from '@restworld/data-services';
import { FileType, useToastMessageContext } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { setWorkerCvAtom, workerAtom } from '../../global-state.worker-data';
import { useEntryPointCheckListContext } from '../../worker-entry-point';
import {
  doesSizeExceedsAtom,
  uploadCVLoadingAtom,
  uploadFilesAtom,
  workerEntryPointErrorsAtom,
} from '../../worker-entry-point/worker-entry-point.atoms';

export function useUploadCV() {
  const workerService = useWorkersService();
  const authContext = useAuth();
  const setWorkerCv = useSetAtom(setWorkerCvAtom);
  const [uploadFiles, setUploadFiles] = useAtom(uploadFilesAtom);
  const [doesSizeExceeds, setSizeExceeds] = useAtom(doesSizeExceedsAtom);
  const [errors, setErrors] = useAtom(workerEntryPointErrorsAtom);
  const [uploadCVLoading, uploadCVLoadingToggler] = useAtom(uploadCVLoadingAtom);
  const workerData = useAtomValue(workerAtom);
  const workerCV = workerData?.data?.cv;
  const { isCVNotUploaded, setIsCVNotUploaded } = useEntryPointCheckListContext();
  const { setToastMessage, toastMessageOptions } = useToastMessageContext();
  const { t } = useTranslation();
  const handleUploadFiles = useCallback(
    (value: FileType) => {
      setUploadFiles(value);
      if (value?.[0] && value?.[0].size / 1000000 > 20) {
        setSizeExceeds(true);
      }
      setIsCVNotUploaded(false);
    },
    [setIsCVNotUploaded, setSizeExceeds, setUploadFiles]
  );

  async function closeTimeout() {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(undefined);
      }, 1000);
    });
  }

  const onUploadFileChange = useCallback(async () => {
    if (uploadFiles?.[0]) {
      const payload = {
        cv: uploadFiles[0],
        workerId: authContext?.user?.id,
      };
      uploadCVLoadingToggler(true);
      try {
        const response = await workerService.uploadCV(payload);
        await closeTimeout();
        setWorkerCv(response?.data?.url);
        setUploadFiles([]);
        setToastMessage({
          ...toastMessageOptions,
          message: t('onboarding-page:info_updated_correctly'),
          duration: 5000,
          visible: true,
          type: 'success',
          position: 'bottom',
          ui: 'alert',
        });
      } catch (err: any) {
        if (err?.['message']?.includes('500')) setErrors({ ...err?.data, statusCode: 500 });
        if (err?.['data']) setErrors({ ...err?.data, statusCode: err.code });
        if (err?.code === 413) setSizeExceeds(true);
        return err;
      } finally {
        uploadCVLoadingToggler(false);
      }
    }
  }, [
    uploadFiles,
    authContext?.user?.id,
    uploadCVLoadingToggler,
    workerService,
    setWorkerCv,
    setUploadFiles,
    setToastMessage,
    toastMessageOptions,
    t,
    setErrors,
    setSizeExceeds,
  ]);
  return {
    onUploadFileChange,
    handleUploadFiles,
    errors,
    doesSizeExceeds,
    uploadCVLoading,
    setErrors,
    isCVNotUploaded,
    uploadFiles,
    setUploadFiles,
    setSizeExceeds,
    isCVUploaded: !!workerCV,
  };
}
